import { Alert, Box, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InvestmentAllocation, InvestmentEntity } from '../../models/InvestmentEntity';
import Investor, { getRemainingAllocatableAmount } from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledButton } from '../styles/StyledButtons';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';

interface EntityAllocationStepProps {
  entity: InvestmentEntity;
  investor: Investor;
  onNext: (allocatedAmount: number) => void;
  onBack: () => void;
  onSkip?: () => void;
  loading?: boolean;
  validationErrors?: { [key: string]: string };
  currentAllocations?: InvestmentAllocation[];
}

export const EntityAllocationStep: React.FC<EntityAllocationStepProps> = ({
  entity,
  investor,
  onNext,
  onBack,
  onSkip,
  loading = false,
  validationErrors = {},
  currentAllocations = [],
}) => {
  const [allocationAmount, setAllocationAmount] = useState<string>('');
  const [localValidationError, setLocalValidationError] = useState<string | null>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  // Check for existing allocation when component mounts or entity changes
  useEffect(() => {
    const existing = currentAllocations.find(
      (allocation) => allocation.investmentEntityId === entity.id,
    );

    if (existing) {
      setAllocationAmount(existing.allocatedAmount.toString());
    }
  }, [entity.id, currentAllocations]);

  const remainingAmount = getRemainingAllocatableAmount(investor);
  const committedInvestment = investor.committedInvestment || 0;
  const totalAllocated = investor.totalAllocatedAmount || 0;

  // Focus on the amount input field when component mounts
  useEffect(() => {
    if (amountInputRef.current && remainingAmount > 0) {
      amountInputRef.current.focus();
    }
  }, [remainingAmount]);

  // Get current allocation for this entity
  const currentEntityAllocation = currentAllocations.find(
    (allocation) => allocation.investmentEntityId === entity.id,
  );
  const currentEntityAmount = Number(currentEntityAllocation?.allocatedAmount ?? 0);

  // Maximum allocation depends on whether this is a new allocation or updating existing
  const maxAllocation =
    currentEntityAmount > 0
      ? committedInvestment // Updating existing: can allocate up to total committed investment
      : remainingAmount; // New allocation: can only allocate remaining funds

  const validationResult = useMemo(() => {
    if (!allocationAmount) {
      return { validAmount: undefined, validationError: undefined };
    }

    const amount = parseFloat(allocationAmount);
    if (isNaN(amount) || amount < 1) {
      return {
        validAmount: undefined,
        validationError: 'Please enter a valid amount of at least $1',
      };
    }

    return { validAmount: amount, validationError: undefined };
  }, [allocationAmount]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAllocationAmount(value);
    setLocalValidationError(null);
  };

  const handleNext = () => {
    if (!allocationAmount.trim()) {
      setLocalValidationError('Please enter an allocation amount');
      return;
    }

    if (validationResult.validationError) {
      setLocalValidationError(validationResult.validationError);
      return;
    }

    const amount = validationResult.validAmount;
    if (!amount || amount < 1) {
      setLocalValidationError('Allocation amount must be at least $1');
      return;
    }

    if (amount > maxAllocation) {
      const errorMessage =
        currentEntityAmount > 0
          ? `Allocation amount cannot exceed ${ExtendedLiquid.currencyMillions(maxAllocation, false)} (total committed investment)`
          : `Allocation amount cannot exceed ${ExtendedLiquid.currencyMillions(maxAllocation, false)} (remaining available funds)`;

      setLocalValidationError(errorMessage);
      return;
    }

    onNext(amount);
  };

  const displayError = localValidationError || validationErrors.allocatedAmount;

  return (
    <>
      <StyledCard tone="tone1">
        <StyledCardContent>
          <Box sx={{ mb: 3 }}>
            <StyledTypography variant="h6" sx={{ mb: 1 }}>
              Allocate Amount to {entity.name}
            </StyledTypography>
            <Typography variant="body2" color="text.secondary">
              Specify how much of the committed investment to allocate to this entity.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Committed Investment:</strong>{' '}
              {ExtendedLiquid.currencyMillions(committedInvestment, false)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Already Allocated:</strong>{' '}
              {ExtendedLiquid.currencyMillions(totalAllocated, false)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              <strong>Remaining Available:</strong>{' '}
              {ExtendedLiquid.currencyMillions(remainingAmount, false)}
            </Typography>
          </Box>

          {remainingAmount <= 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              No remaining funds available for allocation. All committed investment has been
              allocated.
            </Alert>
          )}

          {remainingAmount > 0 && (
            <>
              <TextField
                fullWidth
                label="Allocation Amount"
                value={allocationAmount}
                onChange={handleAmountChange}
                error={Boolean(displayError)}
                helperText={displayError}
                disabled={loading}
                placeholder="Enter amount to allocate"
                inputRef={amountInputRef}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <StyledButton variant="outlined" onClick={onBack} disabled={loading}>
              Back
            </StyledButton>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {onSkip && remainingAmount <= 0 && (
                <StyledButton variant="outlined" onClick={onSkip} disabled={loading}>
                  Skip
                </StyledButton>
              )}

              <StyledButton
                variant="contained"
                onClick={handleNext}
                disabled={loading || remainingAmount <= 0 || !allocationAmount.trim()}
              >
                {loading ? 'Processing...' : 'Allocate'}
              </StyledButton>
            </Box>
          </Box>
        </StyledCardContent>
      </StyledCard>
    </>
  );
};
